import warn from '../../../capabilities/warn';

/**
 * Interface used in add() and remove() methods
 */
interface HpTableFilterInterface {
  columnIndex: number;
  filterText: string;
}

/**
 * A data structure for storing, accessing, and applying table filter values
 */
export class HpTableFilterList {
  /**
   * An Map() of Set()'s
   * Key of Map() is columnIndex
   * Values in Set()'s are filterText set for columnIndex
   *
   * https://javascript.info/map-set
   */
  filters: Map<number, Set<string>>;

  /**
   * constructor()
   */
  constructor() {
    this.filters = new Map();
  }

  /**
   * Initialize and set this.filters if this.filters has not yet been set
   */
  private init(): void {
    if (this.isEmpty()) {
      this.filters = new Map();
    }
  }

  /**
   * Clear all filters and values
   *
   * @return void
   */

  public reset(): void {
    this.filters.clear();
    this.init();
  }

  /**
   * Add a new filter to this
   *
   * @param HpTableFilterInterface
   */
  public add({ columnIndex, filterText }: HpTableFilterInterface): void {
    this.init();

    if (!this.filters.get(columnIndex)) {
      this.filters.set(columnIndex, new Set());
    }

    this.filters.get(columnIndex).add(filterText);
  }

  /**
   * Remove a filter from this
   *
   * @param HpTableFilterInterface
   */
  public remove({ columnIndex, filterText }: HpTableFilterInterface): void {
    if (this.isEmpty()) {
      return;
    }

    if (!this.filters.get(columnIndex)) {
      return;
    }

    this.filters.get(columnIndex).delete(filterText);

    if (this.filters.get(columnIndex).size < 1) {
      this.filters.delete(columnIndex);
    }
  }

  /**
   * Returns the filters that are set for columnIndex
   *
   * @param columnIndex number
   * @returns string[]
   */
  public getColumnFiltersActive(columnIndex: number): string[] {
    if (this.isEmpty()) {
      return null;
    }

    if (!this.filters.get(columnIndex)) {
      return null;
    }

    const activeArray = [];
    this.filters.get(columnIndex).forEach(filter => {
      activeArray.push(filter);
    });

    return activeArray;
  }

  /**
   * Clear all filters from columnIndex
   *
   * @param columnIndex number
   * @returns void
   */
  public clearColumn(columnIndex: number): void {
    if (this.isEmpty()) {
      return;
    }

    if (!this.filters.get(columnIndex)) {
      return;
    }

    this.filters.get(columnIndex).clear();
    this.filters.delete(columnIndex);
  }

  /**
   * Determine if this.filters contains a filter for columnIndex
   *
   * @param columnIndex number
   * @returns boolean
   */
  public containsColumnIndex(columnIndex: number): boolean {
    if (this.isEmpty()) {
      return false;
    }

    if (this.filters.get(columnIndex)) {
      if (this.filters.get(columnIndex).size > 0) {
        return true;
      }
    }

    return false;
  }

  /**
   * Returns boolean if this isEmpty
   * true if no filters are set
   * false if filters are set
   *
   * @returns boolean this isEmpty
   */
  public isEmpty(): boolean {
    if (!this.filters) {
      return true;
    }

    if (this.filters.size) {
      return false;
    }

    return true;
  }

  /**
   * Return boolean if rowArray matches the this.filters
   *
   * Filter rules:
   * between columns - filter is AND
   * same column - filter is OR - if checkboxes/multi filter
   *
   * @param rowArray string[]
   */
  public rowMatch(rowArray: string[]): boolean {
    if (this.isEmpty()) {
      return true;
    }

    let rowMatches = false;

    try {
      this.filters.forEach((filterSet, filterColumnIndex) => {
        const cellText = rowArray[filterColumnIndex];

        if (filterSet.has(cellText)) {
          rowMatches = true;
        } else {
          // rowArray must match a filter in each column
          // break does not work inside forEach()
          throw new Error();
        }
      });
    } catch (error) {
      warn('HpTableFilterList.rowMatch() - error', error);
      return false;
    }

    return rowMatches;
  }
}
